// ConsentScreen — Phase 1 Plan 05; telegram-ui migration Phase 12 Plan 12-02 (Wave 1 эталон).
//
// Flow (бизнес-логика — НЕ трогать, UI-SPEC §10 out-of-scope):
// 1. Returning-member entry-redirect по jwt.cid (bug 2026-06-12).
// 2. Fetches GET /api/v1/legal/dispatcher?locale=<userLocale>&version=1.0.
// 3. Renders Markdown via renderLegalMarkdown (react-markdown — strips HTML).
// 4. Consent switch — must be ticked to enable primary CTA.
// 5. Primary CTA → POST /api/v1/consent (X-Init-Data header) → navigate /register.
//
// Презентация (Phase 12): inline-стили → telegram-ui (List/Section/Title/Card/
// Cell/Switch/Banner/Skeleton/Steps) + НАТИВНАЯ Telegram MainButton (useMainButton).
// Это ПЕРВЫЙ мигрируемый экран — эталон для всех Wave 1/2 экранов:
//   - shell: <List> внутри AppRoot (НЕ inline-стилизованный div с хардкод-шрифтом);
//   - primary CTA: нативная MainButton (НЕ telegram-ui <Button>), Pitfall 5;
//   - ошибки: <Banner type="warning"> (НЕ голый red <p>);
//   - загрузка: <Skeleton> (НЕ «Loading…»);
//   - data-testid сохранены (telegram-ui пробрасывает restProps), тесты выживают;
//   - вне Telegram (dev / jsdom) — fallback <Button> с тем же testid для тестов.

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import {
  Banner,
  Button,
  Card,
  Cell,
  List,
  Section,
  Skeleton,
  Steps,
  Switch,
  Title,
} from "@telegram-apps/telegram-ui";

import { type LegalDocResponse, getLegal, recordConsent } from "../api/consent";
import { useAuth } from "../auth/useAuth";
import { haptic } from "../lib/haptics";
import { decodeJwtPayload } from "../lib/jwt";
import { renderLegalMarkdown } from "../lib/legalRedacted";
import { useMainButton } from "../lib/mainButton";
import { SAFE_AREA_BOTTOM } from "../lib/safeArea";

interface ConsentScreenProps {
  baseUrl: string;
}

export function ConsentScreen({ baseUrl }: ConsentScreenProps): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { initDataRaw, jwt } = useAuth();

  // Returning member (jwt cid claim set after company registration) skips the
  // consent/registration walking skeleton — re-running it ends in
  // POST /companies 409 (bug 2026-06-12). Pending users (cid null) stay.
  useEffect(() => {
    const cid = decodeJwtPayload(jwt)?.cid;
    if (cid != null) {
      navigate("/dispatcher/trips", { replace: true });
    }
  }, [jwt, navigate]);

  const [legal, setLegal] = useState<LegalDocResponse | null>(null);
  const [agreed, setAgreed] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    const lang = i18n.language || "en";
    getLegal(baseUrl, "dispatcher", lang, "1.0")
      .then((res) => setLegal(res))
      .catch((err) => setLoadError((err as Error).message));
  }, [baseUrl, i18n.language]);

  const canContinue = agreed && legal !== null && !submitting;

  const onContinue = async (): Promise<void> => {
    if (!initDataRaw) {
      setSubmitError("initData unavailable — re-open Mini App from Telegram");
      haptic.error();
      return;
    }
    if (!legal) {
      setSubmitError("Privacy policy not loaded yet");
      haptic.error();
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      await recordConsent(baseUrl, initDataRaw, {
        audience: "dispatcher",
        policy_version: legal.version,
        locale: legal.locale,
        legal_basis: "consent",
      });
      haptic.success();
      navigate("/register");
    } catch (err) {
      setSubmitError((err as Error).message);
      haptic.error();
    } finally {
      setSubmitting(false);
    }
  };

  // Native Telegram MainButton — единственный primary-источник на экране
  // (Pitfall 5). Внутри Telegram кнопка снизу; вне — no-op (рендерим fallback).
  useMainButton({
    text: t("consent.continue"),
    onClick: () => {
      void onContinue();
    },
    isEnabled: canContinue,
    isLoading: submitting,
  });

  return (
    <List>
      <Section header={<Steps count={3} progress={0} />}>
        <Title level="1" weight="2" style={{ padding: "8px 22px" }}>
          {t("consent.privacy_policy_title")}
        </Title>

        {loadError && (
          <Banner
            type="section"
            header={t("consent.load_error")}
            description={loadError}
            role="alert"
            data-testid="consent-load-error"
          />
        )}

        {legal === null && !loadError && (
          <Skeleton visible data-testid="consent-loading">
            <div style={{ height: "40vh" }} />
          </Skeleton>
        )}

        {legal && (
          <Card type="plain" style={{ margin: "0 16px" }}>
            <div
              data-testid="legal-markdown"
              style={{ maxHeight: "55vh", overflowY: "auto", padding: 12 }}
            >
              {renderLegalMarkdown(legal.markdown)}
            </div>
          </Card>
        )}
      </Section>

      {legal && (
        <Section>
          <Cell
            Component="label"
            after={
              <Switch
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                data-testid="consent-agree-checkbox"
              />
            }
            multiline
          >
            {t("consent.i_agree")}
          </Cell>
        </Section>
      )}

      {submitError && (
        <Banner
          type="section"
          description={submitError}
          role="alert"
          data-testid="consent-submit-error"
        />
      )}

      {/* Fallback CTA для окружений без нативной MainButton (dev / vitest jsdom).
          Тот же data-testid — тесты используют его; внутри Telegram primary —
          нативная MainButton (выше через useMainButton). */}
      <Section style={{ paddingBottom: SAFE_AREA_BOTTOM }}>
        <Button
          size="l"
          stretched
          disabled={!canContinue}
          loading={submitting}
          onClick={() => {
            void onContinue();
          }}
          data-testid="consent-continue-button"
        >
          {t("consent.continue")}
        </Button>
      </Section>
    </List>
  );
}
